// src/shared/ui/ChartDownloadMenu.tsx
//
// Small, reusable "Download" dropdown for chart image export (PNG / JPEG).
// Styled to blend with the treasury dashboard's segmented toggle pills and
// fully theme-aware (dark/light). Handles its own busy + error state so
// individual chart components stay clean: they just pass onPng / onJpeg.
//
// UX / a11y:
//  - Single unobtrusive "Download" button with a caret; opens a menu.
//  - Closes on outside-click and Escape.
//  - Buttons carry title + visually-hidden text for screen readers.
//  - A failed export surfaces a subtle inline error (never throws to caller).

import { ChevronDown, Download, Loader2 } from 'lucide-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';

interface Props {
    onPng: () => void | Promise<void>;
    onJpeg: () => void | Promise<void>;
    size?: 'sm' | 'md';
    /** Accessible label describing what is being downloaded. */
    label?: string;
    className?: string;
}

export const ChartDownloadMenu: React.FC<Props> = ({
    onPng,
    onJpeg,
    size = 'sm',
    label = 'chart',
    className = '',
}) => {
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState<null | 'png' | 'jpeg'>(null);
    const [error, setError] = useState(false);
    const rootRef = useRef<HTMLDivElement>(null);

    const pad = size === 'sm' ? 'px-2 py-1 text-[11px]' : 'px-3 py-1.5 text-xs';

    // Close on outside-click / Escape.
    useEffect(() => {
        if (!open) return;
        const onDocClick = (e: MouseEvent) => {
            if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
                setOpen(false);
            }
        };
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setOpen(false);
        };
        document.addEventListener('mousedown', onDocClick);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDocClick);
            document.removeEventListener('keydown', onKey);
        };
    }, [open]);

    const run = useCallback(
        async (fmt: 'png' | 'jpeg', fn: () => void | Promise<void>) => {
            setError(false);
            setBusy(fmt);
            try {
                await fn();
                setOpen(false);
            } catch (err) {
                // eslint-disable-next-line no-console
                console.error(`Chart ${fmt.toUpperCase()} export failed`, err);
                setError(true);
            } finally {
                setBusy(null);
            }
        },
        [],
    );

    const itemClass =
        'flex w-full items-center gap-2 px-3 py-1.5 text-xs font-medium text-slate-600 dark:text-slate-300 ' +
        'hover:bg-slate-100 dark:hover:bg-slate-700/60 disabled:opacity-50 disabled:cursor-not-allowed transition-colors';

    return (
        <div ref={rootRef} className={`relative ${className}`}>
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                aria-haspopup="menu"
                aria-expanded={open}
                title={`Download ${label} as image`}
                className={`inline-flex items-center gap-1 font-semibold rounded-md border transition-colors ${pad} ${
                    error
                        ? 'border-rose-300 text-rose-600 dark:border-rose-800 dark:text-rose-400'
                        : 'border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300'
                } bg-white dark:bg-slate-700 hover:bg-slate-50 dark:hover:bg-slate-600`}
            >
                {busy ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                    <Download className="w-3.5 h-3.5" />
                )}
                <span>Download</span>
                <span className="sr-only">{label} as PNG or JPEG image</span>
                <ChevronDown className={`w-3 h-3 transition-transform ${open ? 'rotate-180' : ''}`} />
            </button>

            {open && (
                <div
                    role="menu"
                    className="absolute right-0 z-50 mt-1 w-32 overflow-hidden rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-xl"
                >
                    <button
                        type="button"
                        role="menuitem"
                        disabled={busy !== null}
                        onClick={() => run('png', onPng)}
                        className={itemClass}
                        title="Download PNG"
                    >
                        <span className="font-mono text-[10px] font-bold text-indigo-500">PNG</span>
                        <span>Image</span>
                    </button>
                    <button
                        type="button"
                        role="menuitem"
                        disabled={busy !== null}
                        onClick={() => run('jpeg', onJpeg)}
                        className={itemClass}
                        title="Download JPEG"
                    >
                        <span className="font-mono text-[10px] font-bold text-amber-500">JPG</span>
                        <span>Image</span>
                    </button>
                </div>
            )}

            {error && (
                <span
                    role="alert"
                    className="absolute right-0 top-full mt-1 whitespace-nowrap text-[10px] font-medium text-rose-500"
                >
                    Export failed. Try again.
                </span>
            )}
        </div>
    );
};

export default ChartDownloadMenu;
